import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

const TeamManagementPage = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('members');
  const [teamMembers, setTeamMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  // Mock team members data
  const mockTeamMembers = [
    {
      id: 1,
      name: 'John Smith',
      email: 'john.smith@company.com',
      role: 'admin',
      avatar: null,
      status: 'active',
      last_active: '2025-07-14T20:30:00Z',
      joined_date: '2025-01-15',
      permissions: ['manage_jobs', 'manage_team', 'view_analytics', 'manage_billing'],
      department: 'Operations'
    },
    {
      id: 2,
      name: 'Lisa Johnson',
      email: 'lisa.johnson@company.com',
      role: 'manager',
      avatar: null,
      status: 'active',
      last_active: '2025-07-14T18:45:00Z',
      joined_date: '2025-02-20',
      permissions: ['manage_jobs', 'view_analytics', 'manage_candidates'],
      department: 'HR'
    },
    {
      id: 3,
      name: 'Mike Davis',
      email: 'mike.davis@company.com',
      role: 'recruiter',
      avatar: null,
      status: 'inactive',
      last_active: '2025-07-12T16:20:00Z',
      joined_date: '2025-03-10',
      permissions: ['manage_jobs', 'manage_candidates'],
      department: 'Recruitment'
    }
  ];

  // Mock pending invitations
  const mockInvitations = [
    {
      id: 1,
      email: 'sarah.wilson@company.com',
      role: 'manager',
      invited_by: 'John Smith',
      invited_date: '2025-07-13',
      status: 'pending',
      department: 'Marketing'
    },
    {
      id: 2,
      email: 'alex.brown@company.com',
      role: 'recruiter',
      invited_by: 'Lisa Johnson',
      invited_date: '2025-07-12',
      status: 'expired',
      department: 'HR'
    }
  ];

  const roles = [
    {
      id: 'admin',
      name: 'Administrator',
      description: 'Full access to all features and settings',
      permissions: ['manage_jobs', 'manage_team', 'view_analytics', 'manage_billing', 'manage_settings']
    },
    {
      id: 'manager',
      name: 'Manager',
      description: 'Can manage jobs, candidates, and view analytics',
      permissions: ['manage_jobs', 'view_analytics', 'manage_candidates', 'manage_interviews']
    },
    {
      id: 'recruiter',
      name: 'Recruiter',
      description: 'Can manage jobs and candidates',
      permissions: ['manage_jobs', 'manage_candidates', 'manage_interviews']
    },
    {
      id: 'viewer',
      name: 'Viewer',
      description: 'Read-only access to jobs and analytics',
      permissions: ['view_jobs', 'view_analytics']
    }
  ];

  const permissions = [
    { id: 'manage_jobs', name: 'Manage Jobs', description: 'Create, edit, and delete job postings' },
    { id: 'manage_candidates', name: 'Manage Candidates', description: 'Search, contact, and manage candidates' },
    { id: 'manage_interviews', name: 'Manage Interviews', description: 'Schedule and manage interviews' },
    { id: 'manage_team', name: 'Manage Team', description: 'Invite, remove, and manage team members' },
    { id: 'view_analytics', name: 'View Analytics', description: 'Access hiring analytics and reports' },
    { id: 'manage_billing', name: 'Manage Billing', description: 'Access billing and subscription settings' },
    { id: 'manage_settings', name: 'Manage Settings', description: 'Access company and account settings' },
    { id: 'view_jobs', name: 'View Jobs', description: 'View job postings and applications' }
  ];

  const tabs = [
    { id: 'members', label: 'Team Members', count: mockTeamMembers.length },
    { id: 'invitations', label: 'Invitations', count: mockInvitations.filter(i => i.status === 'pending').length },
    { id: 'roles', label: 'Roles & Permissions', count: roles.length }
  ];

  useEffect(() => {
    setTeamMembers(mockTeamMembers);
    setInvitations(mockInvitations);
  }, []);

  const handleInviteMember = (inviteData) => {
    const newInvitation = {
      id: Date.now(),
      email: inviteData.email,
      role: inviteData.role,
      invited_by: userProfile?.full_name || 'You',
      invited_date: new Date().toISOString().split('T')[0],
      status: 'pending',
      department: inviteData.department
    };
    
    setInvitations(prev => [...prev, newInvitation]);
    setShowInviteModal(false);
  };

  const handleResendInvitation = (invitationId) => {
    setInvitations(prev => prev.map(inv => 
      inv.id === invitationId 
        ? { ...inv, invited_date: new Date().toISOString().split('T')[0], status: 'pending' }
        : inv
    ));
  };

  const handleCancelInvitation = (invitationId) => {
    if (confirm('Are you sure you want to cancel this invitation?')) {
      setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
    }
  };

  const handleUpdateMemberRole = (memberId, newRole) => {
    const role = roles.find(r => r.id === newRole);
    setTeamMembers(prev => prev.map(member => 
      member.id === memberId 
        ? { ...member, role: newRole, permissions: role.permissions }
        : member
    ));
    setShowRoleModal(false);
    setSelectedMember(null);
  };

  const handleRemoveMember = (memberId) => {
    if (confirm('Are you sure you want to remove this team member?')) {
      setTeamMembers(prev => prev.filter(member => member.id !== memberId));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'expired': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-800';
      case 'manager': return 'bg-blue-100 text-blue-800';
      case 'recruiter': return 'bg-green-100 text-green-800';
      case 'viewer': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatLastActive = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Active now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const TeamMemberCard = ({ member }) => (
    <div className="bg-surface rounded-lg border border-border p-6 hover:border-primary hover:shadow-md transition-all duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
            {member.avatar ? (
              <img
                src={member.avatar}
                alt={member.name}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <Icon name="User" size={20} className="text-primary" />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-text-primary">{member.name}</h3>
            <p className="text-text-secondary text-sm">{member.email}</p>
            <p className="text-text-muted text-xs">{member.department}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(member.status)}`}>
            {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
          </span>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(member.role)}`}>
            {roles.find(r => r.id === member.role)?.name || member.role}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
        <div className="flex items-center space-x-2">
          <Icon name="Calendar" size={16} className="text-text-muted" />
          <span className="text-text-secondary">Joined {new Date(member.joined_date).toLocaleDateString()}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Clock" size={16} className="text-text-muted" />
          <span className="text-text-secondary">{formatLastActive(member.last_active)}</span>
        </div>
      </div>

      <div className="mb-4">
        <h4 className="text-sm font-medium text-text-primary mb-2">Permissions</h4>
        <div className="flex flex-wrap gap-2">
          {member.permissions.slice(0, 3).map(permission => (
            <span
              key={permission}
              className="px-2 py-1 bg-secondary-100 text-text-secondary text-xs rounded-full"
            >
              {permissions.find(p => p.id === permission)?.name || permission}
            </span>
          ))}
          {member.permissions.length > 3 && (
            <span className="px-2 py-1 bg-secondary-100 text-text-secondary text-xs rounded-full">
              +{member.permissions.length - 3} more
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center space-x-3">
        <Button
          variant="outline"
          size="sm"
          iconName="Edit"
          onClick={() => {
            setSelectedMember(member);
            setShowRoleModal(true);
          }}
        >
          Edit Role
        </Button>
        <Button
          variant="outline"
          size="sm"
          iconName="MessageSquare"
          onClick={() => navigate(`/messages?contact=${member.name}`)}
        >
          Message
        </Button>
        {member.id !== user?.id && (
          <Button
            variant="outline"
            size="sm"
            iconName="Trash2"
            onClick={() => handleRemoveMember(member.id)}
            className="text-red-600 hover:text-red-700"
          >
            Remove
          </Button>
        )}
      </div>
    </div>
  );

  const InvitationCard = ({ invitation }) => (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-text-primary">{invitation.email}</h3>
          <p className="text-text-secondary text-sm">{invitation.department}</p>
          <p className="text-text-muted text-xs">
            Invited by {invitation.invited_by} on {new Date(invitation.invited_date).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(invitation.status)}`}>
            {invitation.status.charAt(0).toUpperCase() + invitation.status.slice(1)}
          </span>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(invitation.role)}`}>
            {roles.find(r => r.id === invitation.role)?.name || invitation.role}
          </span>
        </div>
      </div>

      <div className="flex items-center space-x-3">
        {invitation.status === 'pending' && (
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            onClick={() => handleResendInvitation(invitation.id)}
          >
            Resend
          </Button>
        )}
        {invitation.status === 'expired' && (
          <Button
            variant="primary"
            size="sm"
            iconName="RefreshCw"
            onClick={() => handleResendInvitation(invitation.id)}
          >
            Resend
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          iconName="X"
          onClick={() => handleCancelInvitation(invitation.id)}
          className="text-red-600 hover:text-red-700"
        >
          Cancel
        </Button>
      </div>
    </div>
  );

  const RoleCard = ({ role }) => (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-text-primary">{role.name}</h3>
          <p className="text-text-secondary text-sm">{role.description}</p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(role.id)}`}>
          {teamMembers.filter(m => m.role === role.id).length} members
        </span>
      </div>

      <div>
        <h4 className="text-sm font-medium text-text-primary mb-2">Permissions</h4>
        <div className="space-y-2">
          {role.permissions.map(permissionId => {
            const permission = permissions.find(p => p.id === permissionId);
            return (
              <div key={permissionId} className="flex items-center space-x-2">
                <Icon name="Check" size={14} className="text-green-600" />
                <span className="text-sm text-text-secondary">{permission?.name}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const InviteModal = () => {
    const [formData, setFormData] = useState({
      email: '',
      role: 'recruiter',
      department: ''
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      handleInviteMember(formData);
      setFormData({ email: '', role: 'recruiter', department: '' });
    };

    if (!showInviteModal) return null;

    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={() => setShowInviteModal(false)}
      >
        <div 
          className="bg-surface rounded-lg p-6 w-full max-w-md mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-text-primary">Invite Team Member</h3>
            <button
              onClick={() => setShowInviteModal(false)}
              className="p-2 hover:bg-secondary-100 rounded-lg"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="colleague@company.com"
              required
            />

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                {roles.map(role => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
              <p className="text-xs text-text-muted mt-1">
                {roles.find(r => r.id === formData.role)?.description}
              </p>
            </div>

            <Input
              label="Department"
              value={formData.department}
              onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
              placeholder="e.g., HR, Marketing, Operations"
            />

            <div className="flex items-center space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowInviteModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                iconName="Send"
              >
                Send Invitation
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const RoleModal = () => {
    const [selectedRole, setSelectedRole] = useState(selectedMember?.role || '');

    const handleSubmit = (e) => {
      e.preventDefault();
      if (selectedMember && selectedRole) {
        handleUpdateMemberRole(selectedMember.id, selectedRole);
      }
    };

    if (!showRoleModal || !selectedMember) return null;

    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={() => setShowRoleModal(false)}
      >
        <div 
          className="bg-surface rounded-lg p-6 w-full max-w-md mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-text-primary">Update Role</h3>
            <button
              onClick={() => setShowRoleModal(false)}
              className="p-2 hover:bg-secondary-100 rounded-lg"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <div className="mb-4 p-3 bg-secondary-50 rounded-lg">
            <p className="font-medium text-text-primary">{selectedMember.name}</p>
            <p className="text-sm text-text-secondary">{selectedMember.email}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">New Role</label>
              <div className="space-y-2">
                {roles.map(role => (
                  <label key={role.id} className="flex items-start space-x-3 p-3 border border-border rounded-lg hover:border-primary cursor-pointer">
                    <input
                      type="radio"
                      name="role"
                      value={role.id}
                      checked={selectedRole === role.id}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium text-text-primary">{role.name}</p>
                      <p className="text-sm text-text-secondary">{role.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowRoleModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                iconName="Save"
              >
                Update Role
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'members':
        return (
          <div className="space-y-6">
            {teamMembers.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="Users" size={48} className="text-text-muted mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-text-primary mb-2">No team members</h3>
                <p className="text-text-secondary mb-4">Invite your first team member to get started.</p>
                <Button
                  variant="primary"
                  iconName="Plus"
                  onClick={() => setShowInviteModal(true)}
                >
                  Invite Team Member
                </Button>
              </div>
            ) : (
              teamMembers.map(member => (
                <TeamMemberCard key={member.id} member={member} />
              ))
            )}
          </div>
        );

      case 'invitations':
        return (
          <div className="space-y-6">
            {invitations.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="Mail" size={48} className="text-text-muted mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-text-primary mb-2">No pending invitations</h3>
                <p className="text-text-secondary">All team member invitations will appear here.</p>
              </div>
            ) : (
              invitations.map(invitation => (
                <InvitationCard key={invitation.id} invitation={invitation} />
              ))
            )}
          </div>
        );

      case 'roles':
        return (
          <div className="space-y-6">
            {roles.map(role => (
              <RoleCard key={role.id} role={role} />
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">

      
      <main className="pt-16 pb-20 md:pb-8">
        <div className="content-container">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-4">
              <button
                onClick={() => navigate('/employer-dashboard')}
                className="p-2 rounded-lg border border-border hover:border-primary hover:bg-primary-50 transition-colors duration-200"
              >
                <Icon name="ArrowLeft" size={20} />
              </button>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">Team Management</h1>
                <p className="text-text-secondary">Manage your team members, roles, and permissions</p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  variant="primary"
                  iconName="Plus"
                  onClick={() => setShowInviteModal(true)}
                >
                  Invite Team Member
                </Button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-surface rounded-lg border border-border mb-6">
            <div className="flex overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors duration-200 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-primary text-primary bg-primary-50'
                      : 'border-transparent text-text-secondary hover:text-text-primary hover:border-secondary-300'
                  }`}
                >
                  <span>{tab.label}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    activeTab === tab.id
                      ? 'bg-primary text-white'
                      : 'bg-secondary-100 text-text-secondary'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          {renderTabContent()}
        </div>
      </main>
<InviteModal />
      <RoleModal />
    </div>
  );
};

export default TeamManagementPage;


